import {OctTable} from "@oceantech/oceantech-ui";
import React, {useContext, useState} from "react";
import {Button} from "react-bootstrap";
import {toast} from "react-toastify";
import TextValidator from "../../component/TextValidator";
import {
  CODE_SUCCESS,
  ERROR_MESSAGE,
  textSystem,
} from "../../utils/Constant";
import {completeRemediationPlan, updateRemediationPlan} from "../services/CorrectiveSubsectionServices";
import {AppContext} from "../../appContext/AppContext";
import {IFile, IViewFile} from "../../models/models";
import {
  handleDownloadFileUtil,
  handleUploadFileUtil,
  handleViewFileUtil,
} from "../../utils/FileUtils";
import ViewFile from "../modals/ViewFile";
import {FileBtnGroup} from "./FileBtnGroup";
import {ICorrectiveSubsectionModalProps} from "../models/CorrectiveSubsectionModels";
import {useCSContext} from "../contexts";
import {ConfirmDialog} from "../../component/ConfirmDialog";

const CorrectiveSubsectionTable = (props: ICorrectiveSubsectionModalProps) => {
  const {
    dataTable,
    setDataTable,
    objectSearch,
    setObjectSearch,
    configTable,
    reload,
    setReload,
  } = props;

  const {setIsLoading} = useContext(AppContext);
  const {isView, itemDetail} = useCSContext();

  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [viewFile, setViewFile] = useState<IViewFile>({
    open: false,
    file: null,
  });

  const handleViewFile = async (file: IFile) => {
    const viewFile = await handleViewFileUtil(file, setIsLoading);
    if (viewFile) {
      setViewFile(viewFile);
    }
  };

  const handleDownloadFile = async (file: IFile) => {
    await handleDownloadFileUtil(file, setIsLoading);
  };

  const handleChangeTable = async (
    index: number,
    value: any,
    field: string,
  ) => {
    const newDataTable = [...dataTable];
    newDataTable[index][field] = value;

    setDataTable(newDataTable);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      let payload: any[] = {
        ...itemDetail,
        actions: dataTable,
      }
      const { data } = await updateRemediationPlan(itemDetail?.id, payload);
      if (data?.code === CODE_SUCCESS) {
        toast.success("Lưu thành công");
      }
    } catch (error) {
      console.error("error", error);
      toast.error(ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteSubsection = async () => {
    try {
      setIsLoading(true);
      const { data } = await completeRemediationPlan(itemDetail?.id);
      if (data?.code === CODE_SUCCESS) {
        toast.success("Hoàn thiện kế hoạch thành công");
        handleCloseConfirmModal();
      }
    } catch (error) {
      console.error("error", error);
      toast.error(ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseConfirmModal = () => {
    setOpenConfirmModal(false);
  }

  const correctiveSubsectionColumns = [
    {
      name: "Tên tiểu mục",
      field: "actionContent",
      headerStyle: {
        width: "600px",
      },
      cellStyle: {
        ...textSystem,
      },
      render: (row: any, index: number, stt: number) => row?.actionContent,
    },
    {
      name: "Bằng chứng",
      field: "evidence",
      cellStyle: {
        width: "200px",
        minWidth: "200px",
        maxWidth: "200px",
        textAlign: "center",
        ...textSystem,
      },
      render: (row: any, index: number, stt: number) => (
        <div className="d-flex align-items-center justify-content-center">
          <label className="custom-file-upload">
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                handleChangeTable(index, file, "evidence");
              }}
            />
            {row?.evidence?.name ? (
              <span className="text-info">{row?.evidence?.name}</span>
            ) : (
              <span className="text-info">
                Tải tệp lên <i className="bi bi-upload text-navy ms-2"></i>
              </span>
            )}
          </label>
          {row?.evidence?.name && (
            <FileBtnGroup
              file={row?.evidence}
              onView={handleViewFile}
              onDownload={handleDownloadFile}
            />
          )}
        </div>
      ),
    },
    {
      name: "Hành động",
      field: "progressNote",
      cellStyle: {
        minWidth: "110px",
        ...textSystem,
      },
      render: (row: any, index: number, stt: number) => (
        <div>
          <TextValidator
            className="spaces width-100"
            name="progressNote"
            value={row?.progressNote || ""}
            onChange={(e: any) => {
              handleChangeTable(index, e?.target?.value, "progressNote");
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <OctTable
          id="searchCriteria"
          className={`table-background-th spaces h-calc-vh-242`}
          data={dataTable}
          columns={correctiveSubsectionColumns}
          searchObject={objectSearch}
          setSearchObject={setObjectSearch}
          fixedColumnsCount={-1}
          noToolbar={true}
          totalPages={configTable.totalPages}
          totalElements={configTable.totalElements}
          numberOfElements={configTable.numberOfElements}
          isShowSelection={false}
          clearToolbar={true}
          noPagination={true}
        />

        {!isView && (
          <div className="position-fixed bottom-0 end-0 p-3 w-100 d-flex justify-content-end border-top gap-2">
            <Button className="btn-fill min-w-100px" type="submit">
              Lưu
            </Button>
            <Button className="btn-fill min-w-100px" onClick={() => setOpenConfirmModal(true)}>
              hoàn thiện kế hoạch
            </Button>
          </div>
        )}
      </form>
      {openConfirmModal && (
        <ConfirmDialog
          show={openConfirmModal}
          message="hoàn thiện kế hoạch"
          yes="Xác nhận"
          onYesClick={handleCompleteSubsection}
          cancel="Hủy"
          onCancelClick={handleCloseConfirmModal}
        />
      )}
      {viewFile?.open && (
        <ViewFile
          show={viewFile?.open}
          file={viewFile?.file}
          onHide={() => setViewFile({open: false, file: null})}
        />
      )}
    </div>
  );
};

export default CorrectiveSubsectionTable;
